import { useBooleanState } from '@mfw/components';
import React from 'react';

import { AnswerQuestionnaireModal } from '../ScheduleCalendar/ScheduleCalendarEventModal/AnswerQuestionnaireModal/AnswerQuestionnaireModal';
import { useDismissedQuestionnairesStore } from '../../store/useDismissedQuestionnairesStore';
import { useScopedPendingQuestionnaires } from '../../hooks/questionnaireSubmissions';

/**
 * The dismissible half of the pending-questionnaire rules — everything that is
 * outstanding but must not lock the app.
 *
 * Two kinds land here:
 *   - a scheduled occurrence in this organization that has come due but
 *     carries no deadline, and
 *   - a form ANOTHER organization is waiting on. That one would lock the app
 *     if its own organization were selected; offering it here is what keeps it
 *     from being silently lost, without letting one organization bar the door
 *     of another (see `AuthenticatedGuard`).
 */
const PendingScheduleQuestionnaireModal = () => {
  const { answerableHere, waitingElsewhere } = useScopedPendingQuestionnaires();
  const { dismissedSubmissionIds, setDismissedSubmissionIds } =
    useDismissedQuestionnairesStore();
  const answerModal = useBooleanState();

  // The organization in context comes first — that is where the client is
  // actually working right now.
  const submissionId = answerableHere?.id ?? waitingElsewhere[0]?.id ?? null;
  const isDismissed =
    submissionId !== null && dismissedSubmissionIds.includes(submissionId);

  const { off } = answerModal;
  const dismiss = React.useCallback(() => {
    // Recording a successfully submitted id too is harmless — it leaves the
    // pending list on its own.
    if (submissionId) {
      setDismissedSubmissionIds((ids) =>
        ids.includes(submissionId) ? ids : [...ids, submissionId],
      );
    }
    off();
  }, [submissionId, setDismissedSubmissionIds, off]);

  React.useEffect(() => {
    if (submissionId && !isDismissed) answerModal.on();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId, isDismissed]);

  if (!submissionId || isDismissed) return null;

  return (
    <AnswerQuestionnaireModal
      answerModal={{ ...answerModal, off: dismiss }}
      submissionId={submissionId}
    />
  );
};

export default PendingScheduleQuestionnaireModal;
